/**
 * Pedido ao fornecedor (ADR 0010, parte 5; docs/adr/0045-pedido-ao-fornecedor.md), usado pela API e
 * pelas telas. O pedido é a cópia fiel da proposta aprovada: aqui só entram dados de logística
 * (previsão e instruções de entrega), a confirmação de envio, o cancelamento e a confirmação do
 * fornecedor quando a proposta venceu. Nada aqui aprova, decide alçadas ou muda valores.
 */
import { z } from 'zod';
import type { PurchaseStatus } from './purchasing.ts';
import { uuid } from './schemas.ts';

export const PURCHASE_ORDER_STATUSES = ['issued', 'sent', 'cancelled'] as const;

/**
 * Quais compras exigem contrato antes do pedido (D-01). Regra do Fabricio (28/10/2026), ainda
 * provisória e configurável (PURCHASING_CONTRACT_RULE): 'recurring' (padrão) = compra com serviço
 * recorrente (pagamento por período); o serviço simples segue só com o pedido, como ordem de
 * serviço. 'services' = qualquer serviço; 'none' = nenhuma.
 */
export const PURCHASE_CONTRACT_RULES = ['recurring', 'services', 'none'] as const;
export type PurchaseContractRule = (typeof PURCHASE_CONTRACT_RULES)[number];
export type PurchaseOrderStatus = (typeof PURCHASE_ORDER_STATUSES)[number];

/** Número que as pessoas veem: PC-0001. */
export function purchaseOrderNumber(n: number): string {
  return `PC-${String(n).padStart(4, '0')}`;
}

/** A proposta venceu na data (sem validade informada, não vence). */
export function proposalExpired(validUntil: string | null, today: string): boolean {
  return validUntil != null && validUntil < today;
}

/** O que quem compra pode fazer com o pedido agora. As telas mostram só esses botões. */
export function purchaseOrderActions(
  requestStatus: PurchaseStatus,
  order: { status: PurchaseOrderStatus } | null,
  buyer: boolean,
) {
  const active = buyer && !!order && order.status !== 'cancelled';
  return {
    /** Emitir: compra "Aprovada" e nenhum pedido ativo. */
    issue: buyer && requestStatus === 'approved' && !active,
    /** Registrar o envio ao fornecedor (uma vez). */
    send: active && order!.status === 'issued',
    /** Ajustar previsão e instruções de entrega (logística, não muda valores). */
    updateDelivery: active,
    cancel: active,
  };
}
export type PurchaseOrderActions = ReturnType<typeof purchaseOrderActions>;

/**
 * Por que o cancelamento do pedido não pode devolver a compra a "Aprovada" (ADR 0045, D3). Sem
 * nenhum motivo, a compra volta a "Aprovada" e pode receber um pedido novo; com algum, fica
 * marcada para tratamento específico. Recebimentos e compromissos entram nas próximas entregas.
 */
export const ORDER_REVERT_BLOCKERS = [
  'approval_not_valid',
  'proposal_changed',
  'has_receipts',
  'has_commitments',
] as const;
export type OrderRevertBlocker = (typeof ORDER_REVERT_BLOCKERS)[number];

export function orderRevertBlockers(input: {
  /** A aprovação da contratação usada na emissão continua a mais recente e aprovada. */
  approvalStillValid: boolean;
  /** A cotação continua decidida, com a mesma proposta escolhida, e ela continua atual. */
  proposalUnchanged: boolean;
  hasReceipts: boolean;
  hasCommitments: boolean;
}): OrderRevertBlocker[] {
  const blockers: OrderRevertBlocker[] = [];
  if (!input.approvalStillValid) blockers.push('approval_not_valid');
  if (!input.proposalUnchanged) blockers.push('proposal_changed');
  if (input.hasReceipts) blockers.push('has_receipts');
  if (input.hasCommitments) blockers.push('has_commitments');
  return blockers;
}

// ----- Entradas -----

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

/**
 * Proposta vencida: quem compra registra que o fornecedor mantém integralmente as condições
 * aprovadas, com a data, a evidência ou referência (ex.: e-mail) e, se houver, o anexo da compra.
 */
export const purchaseOrderValidityInput = z.object({
  keepsApprovedConditions: z.literal(true, {
    error: 'Confirme que o fornecedor mantém todas as condições aprovadas',
  }),
  confirmedOn: z.iso.date(),
  evidence: z.string().trim().min(5, 'Descreva a evidência ou a referência').max(1000),
  attachmentId: uuid.nullable().optional(),
});

export const purchaseOrderIssueInput = z.object({
  expectedDelivery: z.iso.date().nullable().optional(),
  deliveryInstructions: optionalText(2000),
  notes: optionalText(4000),
  validityConfirmation: purchaseOrderValidityInput.nullable().optional(),
  /** Exigida quando o pedido passa o valor do contrato ativo (fluxo C); fica no histórico. */
  contractJustification: z
    .string()
    .trim()
    .min(10, 'Explique em pelo menos 10 caracteres por que o pedido passa o valor do contrato')
    .max(1000)
    .nullable()
    .optional(),
});
export type PurchaseOrderIssueInput = z.infer<typeof purchaseOrderIssueInput>;

export const purchaseOrderSentInput = z.object({
  sentOn: z.iso.date(),
  note: optionalText(1000),
});

export const purchaseOrderDeliveryInput = z.object({
  expectedDelivery: z.iso.date().nullable(),
  deliveryInstructions: optionalText(2000),
});

export const purchaseOrderCancelInput = z.object({
  reason: z.string().trim().min(1, 'Explique o motivo do cancelamento').max(1000),
});

const label = z.string().trim().min(1).max(120);

/** Exportação do pedido em Excel: os textos das colunas no idioma da tela. */
export const purchaseOrderExportInput = z.object({
  labels: z.object({
    sheet: label,
    order: label,
    request: label,
    supplier: label,
    legalEntity: label,
    status: label,
    issuedAt: label,
    sentOn: label,
    expectedDelivery: label,
    paymentTerms: label,
    currency: label,
    freight: label,
    total: label,
    line: label,
    description: label,
    quantity: label,
    unit: label,
    unitPrice: label,
    lineTotal: label,
    statuses: z.record(z.enum(PURCHASE_ORDER_STATUSES), label),
  }),
});
export type PurchaseOrderExportInput = z.infer<typeof purchaseOrderExportInput>;
